import { useEffect, useLayoutEffect, useRef } from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { APP_SCROLL_ID } from "@/lib/scroll";

function toTop() {
  if (typeof document === "undefined") return;
  const scroller = document.getElementById(APP_SCROLL_ID);
  if (scroller) scroller.scrollTop = 0;
  if (typeof window !== "undefined" && window.scrollY > 0) {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

/**
 * Byter man sida ska man landa högst upp – redan innan den nya sidan ritas ut,
 * både när fönstret scrollar (desktop) och när innehållet ligger i app-skalets
 * egen yta (mobil). Bakåtnavigering och länkar till ett ankare lämnas ifred.
 */
export function ScrollToTop() {
  const router = useRouter();
  const state = useRouterState({
    select: (s) => ({
      pathname: s.location.pathname,
      hash: s.location.hash,
      idx: s.location.state?.__TSR_index,
    }),
  });
  const prevPath = useRef<string | null>(null);
  const prevIdx = useRef<number | undefined>(undefined);

  // Vid omladdning eller direktbesök ska man alltid landa högst upp – inte
  // där man råkade stå förra gången.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash) return;
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    toTop();
    const raf = requestAnimationFrame(() => {
      toTop();
      requestAnimationFrame(toTop);
    });
    const t = window.setTimeout(toTop, 250);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, []);

  // Alla vanliga interna länkklick ska flytta innehållet direkt, även när
  // länken pekar på sidan man redan står på (t.ex. loggan på startsidan).
  useEffect(() => {
    const onLinkClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest("a[href]");
      if (!(link instanceof HTMLAnchorElement) || link.target === "_blank" || link.hasAttribute("download")) {
        return;
      }

      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.hash) return;
      toTop();
    };

    document.addEventListener("click", onLinkClick, true);
    return () => document.removeEventListener("click", onLinkClick, true);
  }, []);

  // Så fort en navigering startar: upp till toppen direkt, innan nya sidan ritas.
  useEffect(() => {
    const unsub = router.subscribe("onBeforeNavigate", (e) => {
      const to = e.toLocation;
      if (to.hash) return;
      toTop();
    });
    return unsub;
  }, [router]);

  useLayoutEffect(() => {
    const path = state.pathname;
    const first = prevPath.current === null;
    const changed = prevPath.current !== path;
    const wentBack =
      typeof state.idx === "number" &&
      typeof prevIdx.current === "number" &&
      state.idx < prevIdx.current;

    prevPath.current = path;
    prevIdx.current = state.idx as number | undefined;

    if (first || !changed || wentBack || state.hash) return;

    // Kör synkront före målning, och igen efter att sidan hunnit rendera klart.
    toTop();
    const raf1 = requestAnimationFrame(() => {
      toTop();
      requestAnimationFrame(toTop);
    });
    return () => cancelAnimationFrame(raf1);
  }, [state.pathname, state.hash, state.idx]);

  return null;
}
